import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'moment-timezone';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {faUser} from '@fortawesome/pro-light-svg-icons';

import VisibilityChip from './VisibilityChip';
import {BluePillButton} from '../../common/components/BluePillButton';
import DataGridQuickSearchToolbar from './DataGridQuickSearchToolbar';
import {fetchLessons, selectLessons, selectIsLoading as selectIsBuilderLoading} from '../../redux/slices/builder';
import {
  selectUserGroup,
  selectUserGroups,
  fetchUserGroup,
  fetchUsers,
  selectUser,
  selectUsers,
  selectIsLoading as selectIsUserLoading,
} from '../../redux/slices/user';
import {FEATURES} from '../../constants';
import ImageNoData from '../images/no-data.svg';

import styles from '../css/Lessons.module.css';
import baseStyles from '../../css/Base.module.css';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function SelectLessonDialog({isOpen, handleClose, onSelectLesson}) {
  const dispatch = useDispatch();

  const [activeTabIndex, setActiveTabIndex] = useState(null);
  const [lessonsClone, setLessonsClone] = useState([]); // TODO: Refactor this away
  const [searchText, setSearchText] = useState('');
  const [dataGridToolbarFilterButtonEl, setDataGridToolbarFilterButtonEl] = useState(false);

  const userGroup = useSelector(selectUserGroup);
  const userGroups = useSelector(selectUserGroups);
  const user = useSelector(selectUser);
  const users = useSelector(selectUsers);
  const allLessons = useSelector(selectLessons);
  const isBuilderLoading = useSelector(selectIsBuilderLoading);
  const isUserLoading = useSelector(selectIsUserLoading);

  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);
  const isLoading = isBuilderLoading || isUserLoading;

  const myLessonsFilter = (lesson) => lesson.userID === user._id;
  const sharedWithMeFilter = (lesson) => lesson.userID !== user._id && lesson.visibility === 'USER_GROUP';
  const flamingoFilter = (lesson) => lesson.visibility === 'PUBLIC';
  let filterFn = myLessonsFilter;
  if (activeTabIndex === 1) filterFn = sharedWithMeFilter;
  else if (activeTabIndex === 2) filterFn = flamingoFilter;
  let lessons = allLessons.filter(filterFn).map((lesson) => ({id: lesson._id, ...lesson})); // DataGrid requires unique id property

  // Quick search toolbar BEGIN
  function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = lessons.filter((row) => {
      return Object.keys(row).some((field) => {
        let searchValue;
        if (field === 'tags') searchValue = row[field].map((tag) => tag.name).join(',');
        else if (field === 'visibility') searchValue = row[field] === 'USER_GROUP' ? 'Everyone' : row[field];
        else searchValue = row[field]?.toString() ?? '';

        return searchRegex.test(searchValue);
      });
    });
    setLessonsClone(filteredRows || []);
  };
  // Quick search toolbar END

  useEffect(() => {
    if (isContentProducerGroup) setActiveTabIndex(2);
    else setActiveTabIndex(0);
  }, [isContentProducerGroup]);

  useEffect(() => {
    setLessonsClone([]);
    if (isOpen) {
      if (activeTabIndex === 0) dispatch(fetchLessons({userID: user._id}));
      else if (activeTabIndex === 1) dispatch(fetchLessons({visibility: 'USER_GROUP'}));
      else if (activeTabIndex === 2) {
        const userGroupIDs = isContentProducerGroup ? [userGroup._id] : Object.keys(userGroup.tier.content);
        userGroupIDs.forEach((userGroupID) => dispatch(fetchLessons({userGroupID, visibility: 'PUBLIC'})));
      }
    }
  }, [dispatch, isOpen, activeTabIndex, user._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useDeepCompareEffect(() => {
    fetchMissingLogoEntities();
  }, [lessons]);

  useDeepCompareEffect(() => {
    if (hasAllLogoEntities()) {
      setLessonsClone(lessons);
    }
  }, [lessons, users, userGroups, isBuilderLoading]);

  function hasAllLogoEntities() {
    if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
      const userIDs = Array.from(new Set(lessons.map((lesson) => lesson.userID)));
      return userIDs.every((userID) => Object.keys(users).includes(userID));
    } else if (activeTabIndex === 2) {
      const userGroupIDs = Object.keys(userGroup.tier.content);
      return userGroupIDs.every((userGroupID) => Object.keys(userGroups).includes(userGroupID));
    }
  }

  function fetchMissingLogoEntities() {
    if (lessons.length > 0) {
      if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
        const userIDs = Array.from(new Set(lessons.map((lesson) => lesson.userID))).filter(
          (userID) => !Object.keys(users).includes(userID),
        );
        if (userIDs.length > 0) dispatch(fetchUsers(userIDs));
      } else if (activeTabIndex === 2) {
        const userGroupIDs = Object.keys(userGroup.tier.content);
        userGroupIDs.forEach((userGroupID) => dispatch(fetchUserGroup(userGroupID)));
      }
    }
  }

  function onLessonSelect(lesson) {
    onSelectLesson(lesson);
    handleClose();
    setLessonsClone([]);
  }

  function handleChangeTab(event, newTabIndex) {
    setActiveTabIndex(newTabIndex);
  }

  function renderDialogContent() {
    return (
      <>
        {Number.isInteger(activeTabIndex) && !isContentProducerGroup && (
          <Tabs
            className="m-b-30"
            value={activeTabIndex}
            onChange={handleChangeTab}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="MINE" />
            <Tab label="SHARED WITH ME" />
            <Tab label="FLAMINGO" />
          </Tabs>
        )}

        {isLoading && (
          <div className={baseStyles['progress-container']}>
            <CircularProgress />
          </div>
        )}

        {!isLoading && lessons.length === 0 && (
          <div className={styles.empty}>
            <img src={ImageNoData} alt="No Lessons" className={'m-t-30 m-b-30'} />
          </div>
        )}

        {!isLoading && lessons?.length > 0 && (
          <div style={{height: 'calc(100vh - 195px)', width: '100%'}}>
            <div style={{display: 'flex', height: '100%'}}>
              <div style={{flexGrow: 1}}>
                {/* The remove prop is not a DataGrid / Grid column property */}
                <DataGridPro
                  pagination
                  components={{Toolbar: DataGridQuickSearchToolbar}}
                  componentsProps={{
                    toolbar: {
                      value: searchText,
                      onChange: (event) => requestSearch(event.target.value),
                      clearSearch: () => requestSearch(''),
                      setFilterButtonEl: setDataGridToolbarFilterButtonEl,
                    },
                    panel: {
                      anchorEl: dataGridToolbarFilterButtonEl,
                    },
                  }}
                  rows={lessonsClone}
                  columns={[
                    {
                      field: 'action',
                      type: 'actions',
                      hideable: false,
                      getActions: (params) => [
                        <BluePillButton
                          onClick={(event) => onLessonSelect(params.row)}
                          size="small"
                          sx={{paddingLeft: '15px', paddingRight: '15px'}}
                        >
                          Select
                        </BluePillButton>,
                      ],
                    },
                    {field: 'name', headerName: 'Lesson', width: 150},
                    {field: 'description', headerName: 'Description', width: 250},
                    {
                      field: 'createdAt',
                      headerName: 'Created',
                      width: 150,
                      type: 'dateTime',
                      valueFormatter: (params) => moment(params.value).format('M/D/YYYY h:mm a'),
                    },
                    {
                      field: 'by',
                      headerName: 'By',
                      remove: activeTabIndex === 0,
                      width: 150,
                      renderCell: (params) => {
                        const lessonUser = users[params.row.userID];
                        let imageURL;
                        let name;

                        if ([0, 1].includes(activeTabIndex) || (activeTabIndex === 2 && isContentProducerGroup)) {
                          name = `${lessonUser?.firstName ?? ''} ${lessonUser?.lastName ?? ''}`.trim();
                          imageURL = lessonUser?.avatarURL;
                        } else if (activeTabIndex === 2) {
                          const lessonUserGroup = userGroups[params.row.userGroupID];
                          name = lessonUserGroup.name;
                          imageURL = lessonUserGroup?.logoURL;
                        }
                        const initial = name ? name.charAt(0) : null;

                        return (
                          <>
                            <Avatar src={imageURL} style={{display: 'inline-flex', width: 24, height: 24}}>
                              {initial ?? <FontAwesomeIcon icon={faUser} size="xs" />}
                            </Avatar>
                            <span style={{marginLeft: 3, verticalAlign: 'top'}}>{name}</span>
                          </>
                        );
                      },
                    },
                    {
                      field: 'tags',
                      headerName: 'Tags',
                      width: 300,
                      valueGetter: (params) => params.row.tags.map((tag) => tag.name).join(','),
                      renderCell: (params) => {
                        const {tags} = params.row;
                        return (
                          <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
                            {tags.map((tag) => (
                              <Chip key={tag._id} label={tag.name} />
                            ))}
                          </Stack>
                        );
                      },
                    },
                    {
                      field: 'visibility',
                      headerName: 'Visibility',
                      remove: activeTabIndex === 2,
                      valueGetter: (params) =>
                        params.row.visibility === 'USER_GROUP' ? 'Everyone' : params.row.visibility,
                      renderCell: (params) => <VisibilityChip visibility={params.row.visibility} />,
                    },
                  ].filter((column) => !column.remove)}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{position: 'relative'}} className="bg-dark-blue">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
            Select Lesson
          </Typography>
        </Toolbar>
      </AppBar>

      {renderDialogContent()}
    </Dialog>
  );
}
